import type { Maybe } from '@adornis/base/utilTypes.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import type { StoredFile } from '../../client/utils/indexeddb-file-storage.js';
import type { InterviewSlot } from '../interviews/entities/InterviewSlot.js';
import { RegisterMode } from '../registrationMode.js';
import type { RegisterStudentData } from './api/registerStudentWithSuSForm.js';

/** Day-precision values are canonical calendar days, so they render UTC-pinned - never in the viewer's zone. */
const formatDate = (date: Maybe<Date>) => {
  if (!date) return '-';

  return formatCalendarDay(date, 'dd.MM.yyyy');
};

const formatBoolean = (value: boolean | null | undefined) => {
  if (value === null || value === undefined) return '-';
  return value ? 'Ja' : 'Nein';
};

const EmailDataField = (label: string, value: any) => html`
  <div style="padding: 8px 0; border-bottom: 1px solid #e5e5e5;">
    <p style="display: inline-block; font-weight: bold; margin: 0; min-width: 300px; margin-right: 16px;">${label}:</p>
    <p style="display: inline-block; margin: 0; flex: 1;">${value || '-'}</p>
  </div>
`;

const EmailSectionHeader = (title: string) => html`
  <h3
    style="background-color: #6b7280; color: white; padding: 8px 12px; border-radius: 8px; margin: 16px 0 8px 0; font-size: 16px;"
  >
    ${title}
  </h3>
`;

export const SusEntryFormDataSheet = (
  data: RegisterStudentData,
  schoolName: string,
  elementarySchoolName: string,
  mode: RegisterMode,
  interviewData: Maybe<InterviewSlot>,
  files: Array<StoredFile | AdornisFile>,
  learningDisabilities: Maybe<StoredFile | AdornisFile>,
  sorgerechtsurteil: Maybe<StoredFile | AdornisFile>,
  screen = false,
) => {
  const DataField = screen
    ? (label: string, value: unknown) =>
        html`<div class="grid min-w-0 grid-cols-1 gap-1 border-b border-base-300 py-3 sm:grid-cols-2 sm:gap-4">
          <p class="text-sm font-semibold text-base-content/70">${label}</p>
          <p class="min-w-0 break-words">${value ?? '–'}</p>
        </div>`
    : EmailDataField;
  const SectionHeader = screen
    ? (title: string) => html`<h3 class="mb-2 mt-6 font-semibold">${title}</h3>`
    : EmailSectionHeader;
  return html`
    <div
      class=${screen ? 'w-full min-w-0' : ''}
      style=${screen ? nothing : 'width: 100%; background-color: #ffffff; border-radius: 8px; font-family: Arial, sans-serif;'}
    >
      <!-- Termininformationen -->
      ${
        interviewData
          ? html`
              ${SectionHeader('Termininformationen')}
              <div
                style=${screen ? nothing : 'background-color: #f9fafb; padding: 8px; border-radius: 8px; margin-bottom: 16px;'}
              >
                ${DataField('Beginn', interviewData.startDate ? format(interviewData.startDate, 'dd.MM.yyyy HH:mm') : null)}
                <div>
                  <p>Folgende Unterlagen sind zum Gespräch an unserer Schule mitzubringen:</p>

                  <ul>
                    <li>hochgeladene Zeugnisse im Original</li>
                    <li>ggf. Empfehlung der Grundschule</li>
                    <li>Geburtsurkunde</li>
                    <li>Schwimmabzeichen</li>
                    <li>bei alleinigem Sorgerecht: Bestätigung durch das Jugendamt</li>
                    <li>ggf. Kirchenzugehörigkeit der Eltern</li>
                    <li>ggf. diagnostizierte Gutachten (LRS, Dyskalkulie, ADHS, …)</li>
                  </ul>
                </div>
              </div>
            `
          : nothing
      }

      <!-- Schulinformationen -->
      ${SectionHeader('Schulinformationen')}
      <div
        style=${screen ? nothing : 'background-color: #f9fafb; padding: 8px; border-radius: 8px; margin-bottom: 16px;'}
      >
        ${DataField('Schule', schoolName)} ${DataField('Klassenstufe', data.klassenstufe)}
        ${DataField('Interesse an Muttersprachkursen', data.interestInNativeLanguageCourses)}
        ${DataField('Interesse an Ganztagsschule', formatBoolean(data.interestInWholeDaySchooling))}
      </div>

      <!-- Persönliche Daten -->
      ${SectionHeader('Persönliche Daten des Schülers')}
      <div
        style=${screen ? nothing : 'background-color: #f9fafb; padding: 8px; border-radius: 8px; margin-bottom: 16px;'}
      >
        ${DataField('Vorname', data.firstName)} ${DataField('Nachname', data.lastName)}
        ${DataField('Geschlecht', data.geschlecht)} ${DataField('Geburtsdatum', formatDate(data.birthday))}
        ${DataField('Geburtsort', data.placeofBirth)} ${DataField('Geburtsland', data.countryOfBirth)}
        ${DataField('Staatsangehörigkeit', data.staatsangehoerigkeit)}
        ${DataField('Zweite Staatsangehörigkeit', data.staatsangehoerigkeit2)} ${DataField('Religion', data.religion)}
        ${DataField('Familiensprache ist Deutsch', formatBoolean(data.familyLanguageIsGerman))}
        ${DataField(
          'Eventuelle Geschwister in einer Schule der Schulgesellschaft',
          formatBoolean(data.hasSiblingsAtMartinusSchools),
        )}
      </div>

      <!-- Adressdaten -->
      ${SectionHeader('Adressdaten')}
      <div
        style=${screen ? nothing : 'background-color: #f9fafb; padding: 8px; border-radius: 8px; margin-bottom: 16px;'}
      >
        ${DataField('Straße und Hausnummer', data.streetAndNumber)} ${DataField('Postleitzahl', data.zip)}
        ${DataField('Stadt', data.city)} ${DataField('Stadtteil', data.district)}
      </div>

      <!-- Grundschuldaten -->
      ${
        mode === RegisterMode.REGISTRATION_ELEMENTARY_ELEMENTARY || mode === RegisterMode.REGISTRATION_HIGH_HIGH
          ? html`
              ${SectionHeader('Daten der derzeitigen Schule')}
              <div
                style=${screen ? nothing : 'background-color: #f9fafb; padding: 8px; border-radius: 8px; margin-bottom: 16px;'}
              >
                ${
                  !data.potentialMartinusElementarySchoolName
                    ? DataField('Derzeitige Schule', elementarySchoolName)
                    : nothing
                }
                ${
                  data.potentialMartinusElementarySchoolName
                    ? DataField('Derzeitige Schule', data.potentialMartinusElementarySchoolName)
                    : nothing
                }
                ${DataField('Derzeitige Jahrgangsstufe', data.currentKlassenstufe)}
              </div>
            `
          : mode === RegisterMode.REGISTRATION_ELEMENTARY_HIGH
            ? html`
                ${SectionHeader('Grundschuldaten')}
                <div
                  style=${screen ? nothing : 'background-color: #f9fafb; padding: 8px; border-radius: 8px; margin-bottom: 16px;'}
                >
                  ${
                    !data.potentialMartinusElementarySchoolName
                      ? DataField('Grundschule', elementarySchoolName)
                      : nothing
                  }
                  ${
                    data.potentialMartinusElementarySchoolName
                      ? DataField('Grundschule', data.potentialMartinusElementarySchoolName)
                      : nothing
                  }
                  ${DataField('Letzte Note Deutsch', data.lastGradeGerman)}
                  ${DataField('Letzte Note Mathematik', data.lastGradeMath)}
                  ${DataField('Letzte Note Sachunterricht', data.lastGradeSachunterricht)}
                  ${DataField('Schuljahre nach Grundschule', data.schoolyearsAfterElementary)}
                </div>
              `
            : nothing
      }
      <!-- Dokumente -->
      ${SectionHeader('Dokumente')}
      <div
        style=${screen ? nothing : 'background-color: #f9fafb; padding: 8px; border-radius: 8px; margin-bottom: 16px;'}
      >
        ${
          mode !== RegisterMode.REGISTRATION_NEW_ELEMENTARY
            ? DataField(
                mode === RegisterMode.REGISTRATION_ELEMENTARY_HIGH ? 'Grundschulzeugnisse' : 'Zeugnisse',
                files.length
                  ? files.map(f => (f instanceof AdornisFile ? AdornisFile.getServeName(f) : f.name)).join(', ')
                  : 'Keine',
              )
            : nothing
        }
        ${DataField(
          'Lernbehinderungen',
          learningDisabilities
            ? learningDisabilities instanceof AdornisFile
              ? AdornisFile.getServeName(learningDisabilities)
              : learningDisabilities.name
            : 'Nicht vorhanden',
        )}
        ${DataField(
          'Sorgerechtsurteil',
          sorgerechtsurteil
            ? sorgerechtsurteil instanceof AdornisFile
              ? AdornisFile.getServeName(sorgerechtsurteil)
              : sorgerechtsurteil.name
            : 'Nicht vorhanden',
        )}
      </div>

      <!-- Motivation -->
      ${SectionHeader('Motivation')}
      <div
        style=${screen ? nothing : 'background-color: #f9fafb; padding: 8px; border-radius: 8px; margin-bottom: 16px;'}
      >
        ${DataField('Motivation', data.motivation)}
        ${data.schulwechselMotivation ? DataField('Schulwechsel Grund', data.schulwechselMotivation) : nothing}
      </div>

      <!-- Sorgeberechtigte -->
      ${SectionHeader('Sorgeberechtigte')}
      ${data.sorgeberechtigte.map(
        (sb, index) => html`
          <div
            style=${screen ? nothing : 'background-color: #f9fafb; padding: 8px; border-radius: 8px; margin-bottom: 8px;'}
          >
            <h4
              class=${screen ? 'font-semibold' : nothing}
              style=${screen ? nothing : 'font-weight: bold; margin: 0 0 8px 0; color: #6b7280;'}
            >
              Sorgeberechtigte(r) ${index + 1}
            </h4>
            ${DataField('E-Mail', sb.email)} ${DataField('Titel', sb.title)} ${DataField('Vorname', sb.firstNameSB)}
            ${DataField('Nachname', sb.lastNameSB)} ${DataField('Geschlecht', sb.geschlecht)}
            ${DataField('Telefonnummer', sb.telefonNr)} ${DataField('Straße und Hausnummer', sb.streetAndNumber)}
            ${DataField('Postleitzahl', sb.zipSB)} ${DataField('Stadt', sb.citySB)}
            ${DataField('Stadtteil', sb.districtSB)} ${DataField('Religion', sb.religion)}
            ${DataField('Beruf', sb.jobtitle)}
          </div>
        `,
      )}
    </div>
  `;
};
